import { useState } from 'react';
import Table, { TableProps } from '@cloudscape-design/components/table';
import CollectionPreferences from '@cloudscape-design/components/collection-preferences';
import Link from '@cloudscape-design/components/link';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Icon from '@cloudscape-design/components/icon';
import { useNavigate } from 'react-router-dom';
import { Ticket } from '../types/ticket';
import { TicketStatusBadge } from './TicketStatusBadge';
import { SeverityBadge } from './SeverityBadge';
import { EmptyState } from './EmptyState';

export interface SortState {
  sortingColumn: TableProps.SortingColumn<Ticket>;
  isDescending: boolean;
}

interface Props {
  tickets: Ticket[];
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  sortingState?: SortState;
  onSortingChange?: (state: SortState) => void;
  selectedItems?: Ticket[];
  onSelectionChange?: (items: Ticket[]) => void;
  resummarizingIds?: Set<string>;
}

function getColumnDefinitions(resummarizingIds: Set<string>): TableProps.ColumnDefinition<Ticket>[] {
  return [
  {
    id: 'ticketId',
    header: 'Ticket ID',
    cell: (item) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <TicketIdLink ticketId={item.ticketId} />
        {resummarizingIds.has(item.ticketId) && (
          <span title="AI re-summarization in progress..."><Icon name="gen-ai" variant="warning" /></span>
        )}
      </span>
    ),
    sortingField: 'ticketId',
    width: 170,
  },
  {
    id: 'title',
    header: 'Title',
    cell: (item) => <span style={{ wordBreak: 'break-word' }}>{item.title || '—'}</span>,
    sortingField: 'title',
    width: 300,
    maxWidth: 400,
  },
  {
    id: 'status',
    header: 'Status',
    cell: (item) => <TicketStatusBadge status={item.status} />,
    sortingField: 'status',
    width: 150,
  },
  {
    id: 'severity',
    header: 'Severity',
    cell: (item) => item.severity ? <SeverityBadge severity={item.severity} /> : '—',
    sortingField: 'severity',
    width: 120,
  },
  {
    id: 'originalSeverity',
    header: 'Original Severity',
    cell: (item) => item.originalSeverity ? <SeverityBadge severity={item.originalSeverity} /> : '—',
    sortingField: 'originalSeverity',
    width: 150,
  },
  {
    id: 'assignee',
    header: 'Assignee',
    cell: (item) => <AssigneeCell assignee={item.assignee} />,
    sortingField: 'assignee',
    width: 150,
  },
  {
    id: 'createdAt',
    header: 'Created',
    cell: (item) => item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—',
    sortingField: 'createdAt',
    width: 120,
  },
];
}

const STATIC_COLUMNS = getColumnDefinitions(new Set());
const ALL_COLUMN_IDS = STATIC_COLUMNS.map((c) => c.id!);
const DEFAULT_VISIBLE = ALL_COLUMN_IDS;

const CONTENT_DISPLAY_OPTIONS = STATIC_COLUMNS.map((c) => ({
  id: c.id!,
  label: typeof c.header === 'string' ? c.header : c.id!,
  alwaysVisible: c.id === 'ticketId',
}));

function isIndividualAlias(name: string): boolean {
  // Amazon logins are lowercase alpha only, typically 4-8 chars (e.g., marehal, alice)
  // Groups/oncall have spaces, hyphens, or uppercase (e.g., UDD Team, udd-primary-oncall)
  return /^[a-z]{2,12}$/.test(name);
}

function AssigneeCell({ assignee }: { assignee: string }) {
  if (!assignee) return <>Unassigned</>;
  if (isIndividualAlias(assignee)) {
    return <Link href={`https://phonetool.amazon.com/users/${assignee}`} external>{assignee}</Link>;
  }
  return <>{assignee}</>;
}

function TicketIdLink({ ticketId }: { ticketId: string }) {
  const navigate = useNavigate();
  return (
    <Link onFollow={(e) => { e.preventDefault(); navigate(`/tickets/${ticketId}`); }}>
      {ticketId}
    </Link>
  );
}

export function TicketTable({ tickets, loading, error, onRetry, sortingState, onSortingChange, selectedItems, onSelectionChange, resummarizingIds = new Set() }: Props) {
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE);
  const [wrapLines, setWrapLines] = useState(true);

  if (error) {
    return <EmptyState type="error" message={error} onRetry={onRetry} />;
  }

  const columnDefs = getColumnDefinitions(resummarizingIds);
  const visibleColumnDefs = columnDefs.filter((c) => visibleColumns.includes(c.id!));

  return (
    <Table
      items={tickets}
      columnDefinitions={visibleColumnDefs}
      loading={loading}
      loadingText="Loading tickets..."
      selectionType={onSelectionChange ? 'multi' : undefined}
      isItemDisabled={(item) => resummarizingIds.has(item.ticketId)}
      selectedItems={selectedItems}
      onSelectionChange={({ detail }) => onSelectionChange?.(detail.selectedItems)}
      sortingColumn={sortingState?.sortingColumn}
      sortingDescending={sortingState?.isDescending}
      onSortingChange={({ detail }) => {
        onSortingChange?.({
          sortingColumn: detail.sortingColumn,
          isDescending: detail.isDescending ?? false,
        });
      }}
      variant="embedded"
      stickyHeader
      wrapLines={wrapLines}
      header={<span />}
      empty={<EmptyState type="empty" />}
      trackBy="ticketId"
      preferences={
        <CollectionPreferences
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          preferences={{
            contentDisplay: visibleColumns.map((id) => ({ id, visible: true })),
            wrapLines,
          }}
          onConfirm={({ detail }) => {
            setVisibleColumns(
              (detail.contentDisplay ?? []).filter((c) => c.visible).map((c) => c.id)
            );
            setWrapLines(detail.wrapLines ?? true);
          }}
          contentDisplayPreference={{
            options: CONTENT_DISPLAY_OPTIONS,
          }}
          wrapLinesPreference={{
            label: 'Wrap lines',
            description: 'Wrap long text in table cells',
          }}
        />
      }
    />
  );
}
